import type { RarityName } from 'features/catalog/rarity-config.ts';

export interface PathStep {
  badgeOrs: string[];
  badgeAnds: string[];
}

/** Need `required` owned badges from `badges` (any combination). */
export interface CollectAny {
  badges: string[];
  required: number;
  /** Badges per visual row. A number repeats; `[1, 2, 2]` is one then two then two. Default 3. */
  perRow?: number | readonly number[];
}

export interface Journey {
  name: string;
  id: string;
  rarity: RarityName;
  emoji: string;
  gradientColors?: {
    color1: string;
    color2: string;
  };
  /** Sequential AND/OR steps. Omit when using `collectAny`. */
  path?: PathStep[];
  /** Need `required` of the listed badges. Omit when using `path`. */
  collectAny?: CollectAny;
}

export const JOURNEYS: Journey[] = [
  {
    name: `Culture Quest`,
    id: `culture-quest`,
    rarity: 'rare',
    emoji: '🌎',
    gradientColors: {
      color1: 'rgba(229, 190, 155, 0.8)',
      color2: 'rgba(200, 140, 90, 0.8)',
    },
    collectAny: {
      required: 4,
      perRow: [2, 3, 3],
      badges: ['21yr', '3tgz', 'tnlq', 'qpnd', '9x0m', 'hpai', 'tk16', '5tgz'],
    },
  },
  {
    name: `Talos Trendsetter`,
    id: `talos-trendsetter`,
    rarity: 'common',
    emoji: '☀️',
    gradientColors: {
      color1: 'rgba(255, 45, 85, 0.8)',
      color2: 'rgba(0, 210, 180, 0.8)',
    },
    path: [
      {
        badgeOrs: ['7y79', 'rw0q'],
        badgeAnds: [],
      },
      {
        badgeOrs: [],
        badgeAnds: ['b4uv'],
      },
      {
        badgeOrs: ['x7tl', '7ays'],
        badgeAnds: [],
      },
    ],
  },
  {
    name: `Host of Fun`,
    id: `host-of-fun`,
    rarity: 'rare',
    emoji: '🥳',
    gradientColors: {
      color1: 'rgba(40, 170, 40, 0.8)',
      color2: 'rgba(0, 200, 150, 0.8)',
    },
    path: [
      {
        badgeOrs: [],
        badgeAnds: ['nnm0'],
      },
      {
        badgeOrs: ['jt0d', 'hl4y'],
        badgeAnds: [],
      },
      {
        badgeOrs: ['rt3p', 'htci'],
        badgeAnds: [],
      },
    ],
  },
  {
    name: `Growth Gardener`,
    id: `growth-gardener`,
    rarity: 'common',
    emoji: '🌱',
    gradientColors: {
      color1: 'rgba(100, 70, 30, 0.8)',
      color2: 'rgba(60, 110, 40, 0.8)',
    },
    path: [
      {
        badgeAnds: [],
        badgeOrs: ['tpbo', 'n0w4'],
      },
      {
        badgeAnds: [],
        badgeOrs: ['q6ss', 'ixey', '1a0x'],
      },
      {
        badgeAnds: [],
        badgeOrs: ['fyzv', '1ze3', 'tk16'],
      },
    ],
  },
  {
    name: `Engineer's Dynamics`,
    id: `engineer-dynamics`,
    rarity: 'common',
    emoji: '⚒️',
    gradientColors: {
      color1: 'rgba(34, 43, 99, 0.8)',
      color2: 'rgba(110, 54, 229, 0.8)',
    },
    collectAny: {
      required: 4,
      perRow: 3,
      badges: ['nmyu', 'iqzd', 'b2x1', 'jjqu', 'p12z', 'jdoi', '2j0v', 'hoeg', 'mqpm'],
    },
  },
  {
    name: `Creative Spark`,
    id: `creative-spark`,
    rarity: 'rare',
    emoji: '🎨',
    gradientColors: {
      color1: 'rgba(255, 102, 178, 0.8)',
      color2: 'rgba(255, 133, 51, 0.8)',
    },
    collectAny: {
      required: 4,
      perRow: 3,
      badges: ['dr56', 'krih', 'vib2', 'iuvn', 'y0az', 'sy7t', 'wg99', 'c9da', 'gu53'],
    },
  },
  {
    name: `Full English Breakfast`,
    id: `full-english-breakfast`,
    rarity: 'common',
    emoji: '🇬🇧',
    gradientColors: {
      color1: 'rgba(220, 20, 60, 0.8)',
      color2: 'rgba(255, 105, 180, 0.8)',
    },
    collectAny: {
      required: 4,
      perRow: [2, 3, 3],
      badges: ['fk3y', 'gm0p', 'ifu1', 't61t', 'bskt', 'lvrp', 'c3gi', 'g0x1'],
    },
  },
  {
    name: `Swedish Spirit`,
    id: `swedish-spirit`,
    rarity: 'common',
    emoji: '🇸🇪',
    gradientColors: {
      color1: 'rgba(0, 106, 167, 0.8)',
      color2: 'rgba(254, 204, 0, 0.8)',
    },
    collectAny: {
      required: 3,
      perRow: [1, 2, 2],
      badges: ['qpwb', 'zqtm', 'olof', '3mjs', 'dz9q'],
    },
  },
  {
    name: `Empire State of Mind`,
    id: `empire-state-of-mind`,
    rarity: 'common',
    emoji: '🗽',
    gradientColors: {
      color1: 'rgba(0, 87, 183, 0.8)',
      color2: 'rgba(255, 255, 255, 0.8)',
    },
    collectAny: {
      required: 4,
      perRow: [2, 3, 3],
      badges: ['ziyg', '7y79', 'ao3c', '26vr', 'ao8z', 'jw0k', 'y0az', '1m01'],
    },
  },
  {
    name: `Digital Workday`,
    id: `digital-workday`,
    rarity: 'common',
    emoji: '💻',
    gradientColors: {
      color1: 'rgba(37, 99, 235, 0.8)',
      color2: 'rgba(6, 182, 212, 0.8)',
    },
    collectAny: {
      required: 4,
      badges: ['nwqw', 'lark', 'r2v8', 'hero', 'mxv2', 'vib1', 'keys', 'itit', 'tter'],
    },
  },

  {
    name: `Fitness Fanatic`,
    id: `fitness-fanatic`,
    rarity: 'rare',
    emoji: '💪',
    gradientColors: {
      color1: 'rgba(125, 50, 200, 0.8)',
      color2: 'rgba(200, 200, 200, 0.8)',
    },
    collectAny: {
      required: 4,
      badges: ['rtdk', 'jjqu', 'ao8z', '1m01', 'st3p', 'c3gi', 'ibdm', 'g0x1', 'p5m0'],
    },
  },
  // {
  //   name: `Master of Many Skills`,
  //   id: `master-of-many-skills`,
  //   rarity: 'legendary',
  //   emoji: '️‍🔥',
  //   gradientColors: {
  //     color1: 'rgba(255, 90, 0, 0.8)',
  //     color2: 'rgba(255, 200, 0, 0.8)',
  //   },
  //   path: [
  //     {
  //       badgeOrs: [],
  //       badgeAnds: ['srt1', 'pq4t', 'tter'],
  //     },
  //     {
  //       badgeOrs: [],
  //       badgeAnds: ['r2v8', 'iced'],
  //     },
  //     {
  //       badgeOrs: [],
  //       badgeAnds: ['65ry', 'rtdk', 'ifu1'],
  //     },
  //   ],
  // },
  {
    name: `The Mid Game`,
    id: `the-mid-game`,
    rarity: 'rare',
    emoji: '🎮',
    gradientColors: {
      color1: 'rgba(100, 150, 200, 0.8)',
      color2: 'rgba(150, 100, 200, 0.8)',
    },
    collectAny: {
      required: 5,
      perRow: 2,
      badges: ['86xv', 'srt2', 't4lx', 'rtdk', 'pq4t', '65ry'],
    },
  },
  {
    name: `Talos Influencer`,
    id: `talos-influencer`,
    rarity: 'legendary',
    emoji: '🎤',
    gradientColors: {
      color1: 'rgba(68, 0, 102, 0.8)',
      color2: 'rgba(186, 0, 186, 0.8)',
    },
    path: [
      {
        badgeOrs: [],
        badgeAnds: ['gcr2'],
      },
      {
        badgeOrs: ['0lkm', '6sj5'],
        badgeAnds: [],
      },
      {
        badgeOrs: ['0gfr', 'mgju', 'mh96'],
        badgeAnds: [],
      },
    ],
  },
  {
    name: `Operations Juggernaut`,
    id: `operations-juggernaut`,
    rarity: 'legendary',
    emoji: '🥊',
    gradientColors: {
      color1: 'rgba(110, 0, 0, 0.8)',
      color2: 'rgba(40, 20, 20, 0.8)',
    },
    collectAny: {
      required: 5,
      perRow: 3,
      badges: ['om4e', 'jdoi', 'mmac', '1mew', 'p12z', '4xjt', 'uv79', 'ayyc', 'slg4'],
    },
  },

  {
    name: `Crowd Favorite`,
    id: `crowd-favorite`,
    rarity: 'legendary',
    emoji: '🌟',
    gradientColors: {
      color1: 'rgba(236, 72, 153, 0.8)',
      color2: 'rgba(255, 214, 64, 0.8)',
    },
    collectAny: {
      required: 7,
      perRow: [2, 3, 3],
      badges: ['hoeg', 'n26f', '7ays', 'snp0', '52nh', 'cxaa', 'htci', 'edgc'],
    },
  },
  // {
  //   name: `Good Climb`,
  //   id: `mythic-ascension`,
  //   rarity: 'legendary',
  //   emoji: '👑',
  //   gradientColors: {
  //     color1: 'rgba(138, 43, 226, 0.8)',
  //     color2: 'rgba(255, 215, 0, 0.8)',
  //   },
  //   path: [
  //     { badgeAnds: [], badgeOrs: ['coi1', 'gu8b', 'intr'] },
  //     { badgeAnds: [], badgeOrs: ['hpai', 'srt3', 'h588'] },
  //     { badgeAnds: [], badgeOrs: ['bxbq', 'nogh', 'a1y2'] },
  //   ],
  // },
];

/** Owned badges — arrays still work; Sets are O(1) for hot loops (profiles × journeys). */
export type BadgeOwnership = readonly string[] | ReadonlySet<string>;

const ownsBadge = (badgeIDs: BadgeOwnership, badgeID: string): boolean => {
  if (badgeIDs instanceof Set) return badgeIDs.has(badgeID);
  return (badgeIDs as readonly string[]).includes(badgeID);
};

export const toBadgeIdSet = (badgeIDs: BadgeOwnership): Set<string> => {
  if (badgeIDs instanceof Set) return badgeIDs as Set<string>;
  return new Set(badgeIDs as readonly string[]);
};

export const isPathStepComplete = (pathStep: PathStep, badgeIDs: BadgeOwnership): boolean => {
  const andsComplete = pathStep.badgeAnds.every((badgeID) => ownsBadge(badgeIDs, badgeID));
  const orsComplete =
    pathStep.badgeOrs.length === 0 || pathStep.badgeOrs.some((badgeID) => ownsBadge(badgeIDs, badgeID));
  return andsComplete && orsComplete;
};

export const countOwnedCollectAny = (collectAny: CollectAny, badgeIDs: BadgeOwnership): number =>
  collectAny.badges.reduce((n, id) => n + (ownsBadge(badgeIDs, id) ? 1 : 0), 0);

/** Split collect-any badges into visual rows (`perRow` number or explicit sizes). */
export const chunkCollectAnyBadges = (ids: readonly string[], perRow?: CollectAny['perRow']): string[][] => {
  if (ids.length === 0) return [];
  const sizes: readonly number[] = typeof perRow === 'number' ? [perRow] : (perRow ?? [3]);
  const customRows = Array.isArray(perRow);
  const rows: string[][] = [];
  let i = 0;
  let sizeIndex = 0;
  while (i < ids.length) {
    const n = Math.max(1, sizes[customRows ? Math.min(sizeIndex, sizes.length - 1) : 0] ?? 3);
    rows.push([...ids.slice(i, i + n)]);
    i += n;
    sizeIndex += 1;
  }
  return rows;
};

export const isCollectAnyComplete = (collectAny: CollectAny, badgeIDs: BadgeOwnership): boolean =>
  countOwnedCollectAny(collectAny, badgeIDs) >= collectAny.required;

export const isJourneyCompleted = (journey: Journey, badgeIDs: BadgeOwnership) => {
  if (journey.collectAny) return isCollectAnyComplete(journey.collectAny, badgeIDs);
  return (journey.path ?? []).every((pathStep) => isPathStepComplete(pathStep, badgeIDs));
};

/** Path journeys: completed steps; collectAny: owned toward `required` (capped). */
export const getJourneyProgressCounts = (
  journey: Journey,
  badgeIDs: BadgeOwnership
): { owned: number; required: number } | null => {
  if (journey.collectAny) {
    const required = journey.collectAny.required;
    if (required <= 0) return null;
    const owned = Math.min(countOwnedCollectAny(journey.collectAny, badgeIDs), required);
    return { owned, required };
  }
  const steps = journey.path ?? [];
  if (steps.length === 0) return null;
  const owned = steps.filter((step) => isPathStepComplete(step, badgeIDs)).length;
  return { owned, required: steps.length };
};

/** e.g. `1/3`, `2/4`. */
export const getJourneyProgressLabel = (journey: Journey, badgeIDs: BadgeOwnership): string | null => {
  const progress = getJourneyProgressCounts(journey, badgeIDs);
  if (!progress) return null;
  return `${progress.owned}/${progress.required}`;
};

/**
 * City office journeys with “claimed by others” gatekeeping on later badges.
 * Full English Breakfast, Empire State of Mind (Swedish Spirit is not gated).
 */
export const OFFICE_CITY_JOURNEY_IDS: ReadonlySet<string> = new Set(['full-english-breakfast', 'empire-state-of-mind']);

/** Badge ids on a single path step (AND + OR). */
export const badgeIdsInPathStep = (step: PathStep): string[] => [...step.badgeAnds, ...step.badgeOrs];

/** First-row badges that unlock the rest of an office-city journey in claimed-by-others. */
export const officeCityUnlockBadgeIds = (journey: Journey): string[] => {
  if (journey.collectAny) {
    const rows = chunkCollectAnyBadges(journey.collectAny.badges, journey.collectAny.perRow);
    return rows[0] ?? [];
  }
  const step0 = journey.path?.[0];
  return step0 ? badgeIdsInPathStep(step0) : [];
};

/** All badge ids that count toward a journey (`collectAny` list, or AND + OR path steps). */
export const badgeIdsInJourney = (journey: Journey): string[] => {
  if (journey.collectAny) return [...journey.collectAny.badges];
  const ids: string[] = [];
  for (const step of journey.path ?? []) {
    ids.push(...badgeIdsInPathStep(step));
  }
  return ids;
};

/**
 * Badge ids from journeys whose name matches `query` (case-insensitive substring).
 * Used by claim quick-search so typing / picking a journey surfaces its badges.
 */
export const badgeIdsForJourneyNameQuery = (query: string): Set<string> => {
  const q = query.trim().toLowerCase();
  const ids = new Set<string>();
  if (!q) return ids;
  for (const journey of JOURNEYS) {
    if (!journey.name.toLowerCase().includes(q)) continue;
    for (const id of badgeIdsInJourney(journey)) ids.add(id);
  }
  return ids;
};

/**
 * Whether a badge may appear in “claimed by others” before unlock.
 * Badges outside any journey are always eligible.
 * For office city journeys: first-row badges are always shown;
 * claiming any of them unlocks the rest of that journey. Other journeys
 * have no gatekeeping.
 */
export const isBadgeEligibleForClaimedByOthers = (badgeId: string, badgeIDs: BadgeOwnership): boolean => {
  let appearsInAnyJourney = false;
  let unlocked = false;

  for (const journey of JOURNEYS) {
    const journeyBadgeIds = badgeIdsInJourney(journey);
    if (!journeyBadgeIds.includes(badgeId)) continue;

    appearsInAnyJourney = true;

    if (!OFFICE_CITY_JOURNEY_IDS.has(journey.id)) {
      unlocked = true;
      continue;
    }

    const unlockIds = officeCityUnlockBadgeIds(journey);
    if (unlockIds.length === 0) {
      unlocked = true;
      continue;
    }

    // First-row picks are always available.
    if (unlockIds.includes(badgeId)) {
      unlocked = true;
      continue;
    }

    // Any claimed first-row badge unlocks the rest of the journey.
    if (unlockIds.some((id) => ownsBadge(badgeIDs, id))) unlocked = true;
  }

  if (!appearsInAnyJourney) return true;
  return unlocked;
};

export const isJourneyOneStepAway = (journey: Journey, badgeIDs: BadgeOwnership) => {
  // If already completed, not one step away
  if (isJourneyCompleted(journey, badgeIDs)) {
    return false;
  }

  if (journey.collectAny) {
    return countOwnedCollectAny(journey.collectAny, badgeIDs) === journey.collectAny.required - 1;
  }

  // Check if only one path step is incomplete
  const incompleteSteps = (journey.path ?? []).filter((pathStep) => !isPathStepComplete(pathStep, badgeIDs));

  return incompleteSteps.length === 1;
};

export interface JourneyCompletingBadge {
  badgeId: string;
  journey: Journey;
  type: 'and' | 'or';
}

/** Badge IDs excluded from the "complete a journey" suggestions (e.g. restricted/manual badges). */
const JOURNEY_COMPLETING_EXCLUDED_BADGE_IDS = new Set(['dr56']); // Hackathon Participant

/**
 * Finds badges where collecting that single badge would complete a journey.
 * Path journeys: all other steps must already be satisfied. The one incomplete
 * step must need exactly one badge: either a sole missing AND, or any badge
 * from an unsatisfied OR.
 * Collect-any journeys: one short of `required` — any remaining listed badge.
 */
export const getBadgesThatCompleteJourneys = (badgeIDs: BadgeOwnership): JourneyCompletingBadge[] => {
  const results: JourneyCompletingBadge[] = [];
  const seen = new Set<string>();
  const owned = toBadgeIdSet(badgeIDs);

  for (const journey of JOURNEYS) {
    if (isJourneyCompleted(journey, owned)) continue;

    if (journey.collectAny) {
      if (countOwnedCollectAny(journey.collectAny, owned) !== journey.collectAny.required - 1) continue;
      for (const badgeId of journey.collectAny.badges) {
        if (owned.has(badgeId)) continue;
        if (!JOURNEY_COMPLETING_EXCLUDED_BADGE_IDS.has(badgeId) && !seen.has(badgeId)) {
          seen.add(badgeId);
          results.push({ badgeId, journey, type: 'or' });
        }
      }
      continue;
    }

    const incompleteSteps = (journey.path ?? []).filter((step) => !isPathStepComplete(step, owned));

    if (incompleteSteps.length !== 1) continue;

    const step = incompleteSteps[0];
    const missingAnds = step.badgeAnds.filter((id) => !owned.has(id));
    const orsUnsatisfied = step.badgeOrs.length > 0 && !step.badgeOrs.some((id) => owned.has(id));

    if (missingAnds.length === 1 && !orsUnsatisfied) {
      const badgeId = missingAnds[0];
      if (!JOURNEY_COMPLETING_EXCLUDED_BADGE_IDS.has(badgeId) && !seen.has(badgeId)) {
        seen.add(badgeId);
        results.push({ badgeId, journey, type: 'and' });
      }
    } else if (missingAnds.length === 0 && orsUnsatisfied) {
      for (const badgeId of step.badgeOrs) {
        if (!JOURNEY_COMPLETING_EXCLUDED_BADGE_IDS.has(badgeId) && !seen.has(badgeId)) {
          seen.add(badgeId);
          results.push({ badgeId, journey, type: 'or' });
        }
      }
    }
  }

  return results;
};

export const getJourneyCompletionPercentage = (journey: Journey, badgeIDs: BadgeOwnership) => {
  if (isJourneyCompleted(journey, badgeIDs)) {
    return 100;
  }

  if (journey.collectAny) {
    const { required } = journey.collectAny;
    if (required <= 0) return 0;
    const owned = countOwnedCollectAny(journey.collectAny, badgeIDs);
    return Math.min(99, Math.round((owned / required) * 100));
  }

  const steps = journey.path ?? [];
  const stepCount = steps.length;
  if (stepCount === 0) return 0;

  // Each path step owns an equal slice of the wash (e.g. 1/3 ≈ 33% on Mythic Ascension).
  const stepWeight = 100 / stepCount;
  let totalPercentage = 0;

  for (const pathStep of steps) {
    if (isPathStepComplete(pathStep, badgeIDs)) {
      totalPercentage += stepWeight;
      continue;
    }

    // Incomplete OR steps stay empty until claimed; AND steps fill by claimed share.
    const andTotal = pathStep.badgeAnds.length;
    if (andTotal === 0) continue;
    const claimedAnd = pathStep.badgeAnds.filter((badgeID) => ownsBadge(badgeIDs, badgeID)).length;
    totalPercentage += (claimedAnd / andTotal) * stepWeight;
  }

  return Math.min(100, Math.round(totalPercentage));
};
